import { useState, useEffect } from "react";
import { useSearchState } from "./hooks/useSearchState";
import { useDebouncedSearch } from "./hooks/useDebouncedSearch";
import { FieldSelector } from "./components/FieldSelector";
import { SearchInput } from "./components/SearchInput";
import { ClearButton } from "./components/ClearButton";
import { SubmitButton } from "./components/SubmitButton";
import { SearchBarProps } from "./types";
import { getDefaults } from "./utils/defaults";

export function SearchBar(props: SearchBarProps) {
  const config = getDefaults(props);
  
  // State management (URL params)
  const {
    searchValue: urlSearchValue,
    searchField: urlSearchField,
    updateSearch,
    clearSearch,
    hasSearchValue: urlHasSearchValue
  } = useSearchState({
    urlParamStrategy: config.urlParamStrategy
  });

  // Local state for manual trigger (to allow typing without immediate URL update)
  const [localSearch, setLocalSearch] = useState(urlSearchValue);
  const [localField, setLocalField] = useState(urlSearchField);

  // Sync local state with URL when URL changes externally (e.g., browser
  // back/forward, or a debounced commit). Only "instant" reads straight from
  // the URL; manual + debounced drive the input off local state so typing stays
  // responsive and the controlled input never lags behind keystrokes.
  useEffect(() => {
    if (config.trigger !== "instant") {
      setLocalSearch(urlSearchValue);
      setLocalField(urlSearchField);
    }
  }, [urlSearchValue, urlSearchField, config.trigger]);

  const isManual = config.trigger === "manual";
  const isUrlBound = config.trigger === "instant";
  const searchValue = isUrlBound ? urlSearchValue : localSearch;
  const searchField = isUrlBound ? urlSearchField : localField;
  const hasSearchValue = isUrlBound ? urlHasSearchValue : !!localSearch.trim();

  // Debounced search for auto-trigger
  const triggerDebouncedSearch = useDebouncedSearch({
    enabled: config.trigger === "debounced",
    delay: config.debounceMs,
    onSearch: (value, field) => {
      updateSearch(value, field);
      config.onSearchChange?.(value, field);
    }
  });

  // Handle search input change
  const handleSearchChange = (value: string) => {
    const trigger = config.trigger;
    
    if (trigger === "manual") {
      const wasNotEmpty = !!localSearch.trim();
      const isEmpty = !value.trim();
      
      // Update local state
      setLocalSearch(value);
      
      // Auto-trigger when search becomes empty (via backspace) if enabled
      if (config.triggerInstantOnClear && wasNotEmpty && isEmpty) {
        updateSearch("", searchField);
        config.onSearchChange?.("", searchField);
      }
    } else if (trigger === "instant") {
      // Immediate update to URL
      updateSearch(value, searchField);
      config.onSearchChange?.(value, searchField);
    } else if (trigger === "debounced") {
      // Update local state immediately for responsive UI
      setLocalSearch(value);
      // Debounced update to URL
      triggerDebouncedSearch(value, searchField);
    }
  };

  // Handle field selector change
  const handleFieldChange = (field: string) => {
    if (isManual) {
      setLocalField(field);
    } else {
      updateSearch(searchValue, field);
      if (config.trigger !== "manual") {
        config.onSearchChange?.(searchValue, field);
      }
    }
  };

  // Handle clear button
  const handleClear = () => {
    if (isManual) {
      setLocalSearch("");
      setLocalField("all");
      // Auto-trigger when clearing in manual mode if enabled
      if (config.triggerInstantOnClear) {
        updateSearch("", "all");
        config.onClear?.();
        config.onSearchChange?.("", "all");
      }
    } else {
      clearSearch();
      config.onClear?.();
      config.onSearchChange?.("", "all");
    }
  };

  // Handle submit (for manual trigger)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (config.trigger === "manual") {
      updateSearch(localSearch, localField);
      config.onSearchChange?.(localSearch, localField);
    }
  };

  // Show clear button only when there's text
  const showClearButton = config.clearButton.enabled && hasSearchValue;
  
  // Show submit button only for manual trigger
  const showSubmitButton = config.trigger === "manual";

  return (
    <form 
      onSubmit={handleSubmit}
      className={`flex items-center gap-2 w-full ${config.className}`}
    >
      {/* Field Selector (optional) */}
      {config.fieldSelector?.enabled && (
        <FieldSelector
          value={searchField}
          config={config.fieldSelector}
          onChange={handleFieldChange}
          disabled={config.disabled || config.loading}
        />
      )}

      {/* Search Input */}
      <SearchInput
        value={searchValue}
        placeholder={config.placeholder}
        onChange={handleSearchChange}
        disabled={config.disabled || config.loading}
        validation={config.validation}
        showClearInside={showClearButton && config.clearButton.position === "inside"}
        onClearInside={handleClear}
      />

      {/* Clear Button (outside) */}
      {showClearButton && config.clearButton.position === "outside" && (
        <ClearButton
          onClick={handleClear}
          config={config.clearButton}
          disabled={config.disabled || config.loading}
        />
      )}

      {/* Submit Button */}
      {showSubmitButton && (
        <SubmitButton
          config={config.submitButton}
          disabled={config.disabled || config.loading}
        />
      )}
    </form>
  );
}

